import { t } from '@/i18n';

/*
 * The invite, as data. No Vue, no DOM.
 *
 * The origin is a parameter rather than something this module reads off `window`, for the same
 * reason datetime.ts takes the locale and zone: a value only the environment supplies is one a
 * unit test can stub and an end-to-end run cannot, which is how the currency default in
 * docs/STATUS.md shipped green and broke two Playwright specs.
 */

/**
 * The link that gets shared — and the one URL in this app that is not a router path.
 *
 * `/i/{code}` is served by the API, not the SPA: it answers HTML carrying `og:` tags that name
 * *this* pool, its price and how full it is, then bounces a person straight on to
 * `/join/{code}`. Sharing the router path directly is what made every invite in a group chat
 * unfurl as the same anonymous "Rugby Squares" card, on the product's only distribution
 * mechanism. A crawler runs no JavaScript, so the tags cannot come from the SPA at all —
 * docs/decisions/0017-per-game-link-preview.md.
 *
 * The destination is still the join screen rather than `/game/{code}`: a player following a link
 * has no square yet, and the join screen sends them on to the grid once they have a name.
 */
export const inviteUrl = (code: string, origin: string): string =>
  // Trailing slashes matter here — `//i/CODE` is a protocol-relative URL, not a path.
  `${origin.replace(/\/+$/, '')}/i/${code}`;

/** What a share sheet is handed. The field names are the Web Share API's. */
export interface Invite {
  title: string;
  text: string;
  url: string;
}

/**
 * Wording rules, both learned rather than obvious:
 *
 * - **Role-neutral.** The host and any player share the same block, so "my pool" would be wrong
 *   half the time — and nobody edits the text a share sheet prefills.
 * - **The code goes in the text as well as the URL.** Plenty of share targets keep one and drop
 *   the other, and a recipient with the code can always type it in on the join screen.
 */
export function inviteFor(game: { code: string; name?: string | null }, origin: string): Invite {
  const name = game.name?.trim();

  /*
   * Translated, like every other sentence in the app. This was hard-coded English in all three
   * languages — and it is the most-read text in the product, because it is what lands in the
   * group chat rather than what one person sees on a screen.
   *
   * The game's *name* is never translated, for the same reason team names are not: it is
   * somebody's own words. Only the sentence around it moves.
   */
  return {
    title: name ? t('share.inviteTitleNamed', { name }) : t('share.inviteTitle'),
    text: name
      ? t('share.inviteTextNamed', { name, code: game.code })
      : t('share.inviteText', { code: game.code }),
    url: inviteUrl(game.code, origin),
  };
}
